import { useMemo, useState } from "react";
import { PageRenderer } from "@/components/sections/PageRenderer";
import { SectionTitle } from "@/components/common/SectionTitle";
import { PackageCard } from "@/components/cards/PackageCard";
import { AsyncState } from "@/components/common/Loader";
import { usePublicContent } from "@/lib/useContent";
import { useSite } from "@/context/SiteContext";
import { cn } from "@/lib/utils";
import { Link } from "react-router-dom";
import { ShieldCheck, Smartphone, CalendarCheck } from "lucide-react";

const FILTERS = [
  "All",
  "Performance",
  "Fitness",
  "Camps / Clinics",
  "Drop-In Options",
  "Mavs Volleyball",
  "Online Coaching",
];

export default function Packages() { return <PageRenderer pageKey="packages" />; }

export const PackagesBody = () => {
  const { data, loading } = usePublicContent("packages");
  const { settings } = useSite();
  const [active, setActive] = useState("All");

  const filtered = useMemo(() => {
    if (active === "All") return data;
    return data.filter((p) => p.category === active);
  }, [data, active]);

  return (
    <div data-testid="packages-page" className="pb-24 lg:pb-0">
      {/* hero via PageRenderer */}

      <section className="bg-background py-16 sm:py-20">
        <div className="mx-auto w-full max-w-7xl px-4 sm:px-6">
          <SectionTitle
            eyebrow="Find Your Plan"
            title="Compare Packages"
            subtitle="Filter by program type, then check out or book an intro in one tap."
          />

          {/* Category filter tabs */}
          <div
            className="mt-8 flex flex-wrap gap-2"
            role="tablist"
            aria-label="Package categories"
            data-testid="package-filter-tabs"
          >
            {FILTERS.map((f) => {
              const isActive = active === f;
              return (
                <button
                  key={f}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  onClick={() => setActive(f)}
                  data-testid={`package-filter-${f.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}
                  className={cn(
                    "rounded-full border px-4 py-2 font-condensed text-xs font-semibold uppercase tracking-[0.12em] transition-colors duration-200 active:scale-[0.98]",
                    isActive
                      ? "border-[hsl(var(--brand-red))] bg-brand text-white"
                      : "border-border bg-transparent text-muted-foreground hover:border-[hsl(var(--brand-red))] hover:text-foreground"
                  )}
                >
                  {f}
                </button>
              );
            })}
          </div>

          {/* Package grid (comparison layout on desktop) */}
          <div className="mt-10">
            <AsyncState loading={loading} empty={filtered.length === 0} emptyTitle="No packages in this category" emptyDescription="Try a different filter.">
              <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {filtered.map((p) => <PackageCard key={p.id} item={p} />)}
              </div>
            </AsyncState>
          </div>

          {/* Existing client note */}
          <div
            id="packages-client"
            data-testid="existing-client-note"
            className="scroll-mt-24 mt-12 flex flex-col items-start gap-4 rounded-2xl border border-border bg-card/50 p-6 sm:flex-row sm:items-center sm:justify-between sm:p-8"
          >
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-brand text-white">
                <Smartphone className="h-5 w-5" />
              </div>
              <div>
                <h3 className="font-condensed text-lg font-semibold uppercase tracking-wide">Already a Client?</h3>
                <p className="mt-1 text-sm text-muted-foreground">
                  Existing clients can manage booking and purchases inside the Trilogy app / client portal.
                </p>
              </div>
            </div>
            <a
              href={settings.client_portal_url || "https://train.trilogyfitpt.com"}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="client-portal-link"
              className="inline-flex shrink-0 items-center justify-center rounded-md border border-border px-5 py-3 text-sm font-semibold uppercase tracking-wide transition-colors duration-200 hover:bg-accent active:scale-[0.98]"
            >
              Open Client Portal
            </a>
          </div>

          <p className="mt-8 flex items-center justify-center gap-2 text-center text-sm text-muted-foreground">
            <ShieldCheck className="h-4 w-4 text-brand" /> Checkout is handled securely on Exercise.com — no card details are stored on this site.
          </p>
          <p className="mt-3 text-center text-xs text-muted-foreground" data-testid="packages-policy-note">
            Purchases are subject to our{" "}
            <Link to="/policy/terms" className="text-brand hover:underline">Terms</Link>,{" "}
            <Link to="/policy/refund" className="text-brand hover:underline">Refund Policy</Link>,{" "}
            <Link to="/policy/cancellation" className="text-brand hover:underline">Cancellation Policy</Link>, and{" "}
            <Link to="/policy/membership-commitment" className="text-brand hover:underline">Membership Commitment Policy</Link>.
          </p>
        </div>
      </section>

      {/* Mobile sticky Book Intro CTA (sits above the global mobile nav bar) */}
      <a
        href={settings.exercise_booking_url || "https://train.trilogyfitpt.com"}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="packages-mobile-book-intro"
        className="fixed inset-x-3 bottom-[4.75rem] z-40 inline-flex items-center justify-center gap-2 rounded-md bg-brand px-5 py-3.5 text-sm font-semibold uppercase tracking-wide text-white shadow-lg transition-colors duration-200 hover:bg-[hsl(var(--brand-red-deep))] active:scale-[0.99] lg:hidden"
      >
        <CalendarCheck className="h-4 w-4" /> Book Intro Workout
      </a>
    </div>
  );
}
